import { RiskLevelEnum, type RegionalOverviewV2Properties } from 'querys/regional-overview/regional-overview.query.models';
import {
  ALL_FILTER_VALUE,
  RegionalOverviewFilterType,
  buildFilter,
  type Filter
} from '@features/regional-overview/regional-overview.models';
import { computeCalculatedRisk, isAllOrEmpty } from './builders.utils';

const RISK_LEVEL_VALUES: string[] = [
  String(RiskLevelEnum.LOW),
  String(RiskLevelEnum.MEDIUM),
  String(RiskLevelEnum.HIGH),
  String(RiskLevelEnum.NO_RISK)
];

const RISK_LEVEL_SORT_ORDER: string[] = [
  String(RiskLevelEnum.HIGH),
  String(RiskLevelEnum.MEDIUM),
  String(RiskLevelEnum.LOW),
  String(RiskLevelEnum.NO_RISK)
];

export const buildRiskLevelFilter = <P extends RegionalOverviewV2Properties>(): Filter =>
  buildFilter<P>({
    type: RegionalOverviewFilterType.RISK_LEVEL,
    dependsOn: [RegionalOverviewFilterType.DISEASE],
    defaultValue: [ALL_FILTER_VALUE],
    includeAllOption: true,
    translatable: true,
    sortOptions: options => options.slice().sort((a, b) => RISK_LEVEL_SORT_ORDER.indexOf(a.value) - RISK_LEVEL_SORT_ORDER.indexOf(b.value)),
    getValues: () => RISK_LEVEL_VALUES,
    matches: (feature, value) => {
      if (isAllOrEmpty(value)) return true;
      const selection = value as string[];
      const calculatedRisk = feature.properties?.calculatedRisk;
      if (calculatedRisk === undefined) return false;
      return selection.includes(String(calculatedRisk));
    },
    prepare: (feature, value) => {
      const risks = feature.properties?.values?.risks;
      if (!risks) return feature;

      const nextProperties = feature.properties ? { ...feature.properties } : undefined;
      if (!nextProperties || !nextProperties.values) return feature;

      let narrowedDiseases = risks.diseases ?? [];
      if (!isAllOrEmpty(value)) {
        const selection = new Set((value as string[]).map(Number));
        narrowedDiseases = narrowedDiseases.filter(disease => selection.has(disease.risk));
      }

      const calculatedRisk = computeCalculatedRisk(narrowedDiseases, Boolean(nextProperties.values.error));

      nextProperties.calculatedRisk = calculatedRisk;
      nextProperties.values = {
        ...nextProperties.values,
        risks: { ...risks, diseases: narrowedDiseases, risk: calculatedRisk as RiskLevelEnum }
      };
      return { ...feature, properties: nextProperties };
    }
  });
